import React, { useEffect, useRef } from 'react';

/**
 * Simple confirm dialog styled for Vault.
 * Replaces window.confirm for destructive actions.
 */
const VaultConfirmModal = ({
  open,
  title = 'Confirm',
  message,
  confirmLabel = 'Delete',
  cancelLabel = 'Cancel',
  busy = false,
  onConfirm,
  onCancel,
}) => {
  const cancelRef = useRef(null);

  useEffect(() => {
    if (!open) return undefined;
    const prev = document.activeElement;
    cancelRef.current?.focus();

    const onKeyDown = (e) => {
      if (e.key === 'Escape' && !busy) {
        e.preventDefault();
        onCancel?.();
      }
    };
    document.addEventListener('keydown', onKeyDown);
    return () => {
      document.removeEventListener('keydown', onKeyDown);
      if (prev && typeof prev.focus === 'function') prev.focus();
    };
  }, [open, busy, onCancel]);

  if (!open) return null;

  return (
    <div
      className="vault-modal-backdrop"
      role="presentation"
      onClick={() => {
        if (!busy) onCancel?.();
      }}
    >
      <div
        className="vault-modal"
        role="dialog"
        aria-modal="true"
        aria-labelledby="vault-confirm-title"
        aria-describedby="vault-confirm-message"
        onClick={(e) => e.stopPropagation()}
      >
        <h2 id="vault-confirm-title" className="vault-modal-title">
          {title}
        </h2>
        {message ? (
          <p id="vault-confirm-message" className="vault-modal-message">
            {message}
          </p>
        ) : null}
        <div className="vault-modal-actions">
          <button
            ref={cancelRef}
            type="button"
            className="vault-compose-cancel"
            onClick={onCancel}
            disabled={busy}
          >
            {cancelLabel}
          </button>
          <button
            type="button"
            className="vault-modal-confirm"
            onClick={onConfirm}
            disabled={busy}
          >
            {busy ? 'Deleting…' : confirmLabel}
          </button>
        </div>
      </div>
    </div>
  );
};

export default VaultConfirmModal;
